// Global registry to track all active requests
const activeRequests = new Set<AbortController>()

// Function to cancel all active requests (called on logout)
export function cancelAllRequests() {
  console.log(`🚫 Cancelling ${activeRequests.size} active requests`)
  activeRequests.forEach((controller) => {
    try {
      controller.abort()
    } catch (error) {
      // Ignore errors when aborting
    }
  })
  activeRequests.clear()
}

// Helper function to check if an error is a cancelled request
export function isCancelledRequest(error: unknown): boolean {
  return error instanceof Error && error.message === "Request was cancelled"
}

// Helper function to handle cancelled requests uniformly
export function handleCancelledRequest(error: unknown, operation: string): boolean {
  if (isCancelledRequest(error)) {
    console.log(`[v0] ${operation} was cancelled (likely due to logout)`)
    return true
  }
  return false
}

// Helper function to handle 401 authentication errors and redirect to login
// Only redirects if not already on the login page (to allow login errors to be displayed)
function handleUnauthorizedError(): void {
  // Don't redirect if we're on the login page (user is trying to login)
  const isOnLoginPage = typeof window !== "undefined" && window.location.pathname === '/login'
  
  if (!isOnLoginPage) {
    console.log("[v0] 401 Authentication error detected, redirecting to login")
    // Clear auth data
    if (typeof window !== "undefined") {
      localStorage.removeItem("auth_token")
      localStorage.removeItem("refresh_token")
      localStorage.removeItem("user_data")
      // Redirect to login
      window.location.href = "/login"
    }
  }
}

import { isEndpointAllowed, ApiAccessError } from "./api-protection"

export async function httpJson<T>(url: string, init: RequestInit = {}, timeoutMs = 180000): Promise<T> {
  // Use external AbortController if provided, otherwise create new one
  const controller = init.signal ? null : new AbortController();
  const timeout = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  // Register this request only if we created the controller
  if (controller) {
    activeRequests.add(controller);
  }

  try {
    // Automatically add authentication token if available
    const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
    const userData = typeof window !== "undefined" ? localStorage.getItem("user_data") : null

    if (userData && token) {
      try {
        const user = JSON.parse(userData)
        const userRole = user.role || "internal"

        // Extract endpoint path from URL for access control
        const urlObj = new URL(url)
        const endpoint = urlObj.pathname

        if (!isEndpointAllowed(endpoint, userRole)) {
          throw new ApiAccessError(
            `Access denied: Role '${userRole}' cannot access endpoint '${endpoint}'`,
            endpoint,
            userRole,
            ["internal"], // Default required role
          )
        }
      } catch (parseError) {
        console.warn("⚠️ [v0] Could not parse user data for API access check:", parseError)
        // Continue with request if user data parsing fails
      }
    }

    const isFormData = init.body instanceof FormData
    const headers = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
      ...(token && { Authorization: `Bearer ${token}` }),
    }

    // Use external signal if provided, otherwise use internal controller signal
    const signal = init.signal || (controller ? controller.signal : undefined);
    const res = await fetch(url, { ...init, headers, signal });
    const text = await res.text();

    // Preserve prior logic: try JSON, fallback to text, same error semantics
    let data: any = null
    try {
      data = text ? JSON.parse(text) : null
    } catch {
      /* keep text */
    }

    if (!res.ok) {
      // Handle 401 authentication errors globally
      if (res.status === 401) {
        // Check if this is a login attempt (POST to /auth/token/)
        const isLoginRequest = url.includes('/auth/token/') && init.method === 'POST'
        const isOnLoginPage = typeof window !== "undefined" && window.location.pathname === '/login'
        
        if (isLoginRequest && isOnLoginPage) {
          // This is a login attempt with invalid credentials
          // Show user-friendly message for credential errors
          const backendMessage = (data && (data.detail || data.error)) || ""
          const lowerMessage = backendMessage.toLowerCase()
          
          // Check if it's a credentials-related error
          const isCredentialsError = lowerMessage.includes("account") || 
                                    lowerMessage.includes("credentials") ||
                                    lowerMessage.includes("password") ||
                                    lowerMessage.includes("email") ||
                                    lowerMessage.includes("invalid") ||
                                    lowerMessage.includes("incorrect") ||
                                    lowerMessage.includes("wrong")
          
          if (isCredentialsError) {
            // User-friendly message for common credential errors
            throw new Error("Wrong email or password. Try again")
          } else if (backendMessage) {
            // If backend has a message but it's not credentials-related, show it
            throw new Error(backendMessage)
          } else {
            // Fallback if no message from backend
            throw new Error("Wrong email or password. Try again")
          }
        } else {
          // Not a login attempt or not on login page - token expired or unauthorized
          // Always redirect with generic message
          handleUnauthorizedError()
          throw new Error("Authentication credentials were not provided")
        }
      }
      const errorMsg = data && (data.detail || data.error)
      if (errorMsg) {
        const error = new Error(errorMsg)
        if (res.status === 400) {
          (error as any).isValidationError = true
        }
        throw error
      }
      if (data && (Array.isArray(data) || typeof data === 'object')) {
        const error = new Error(JSON.stringify(data))
        if (res.status === 400) {
          (error as any).isValidationError = true
        }
        throw error
      }
      throw new Error(`Service temporarily unavailable (${res.status}). Please try again later.`)
    }
    return (data ?? (text as unknown)) as T
  } catch (error) {
    // If request was aborted, throw a more specific error
    if (controller && controller.signal.aborted) {
      throw new Error('Request was cancelled');
    }
    
    // Handle network errors (Failed to fetch)
    if (error instanceof TypeError && error.message.includes("fetch")) {
      console.error("[httpJson] Network error:", error)
      throw new Error("Network error: Unable to connect to the server. Please check your internet connection and try again.")
    }
    
    // Handle authentication errors from error message (fallback)
    if (error instanceof Error && (
      error.message.includes("Authentication credentials were not provided") ||
      error.message.includes("401")
    )) {
      handleUnauthorizedError()
    }
    
    throw error
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
    // Remove this request from active registry only if we created the controller
    if (controller) {
      activeRequests.delete(controller);
    }
  }
}
